import { StrictMode, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import JSZip from "jszip";
import {
  Apple,
  CheckCircle2,
  Clipboard,
  Download,
  FileArchive,
  FileCode2,
  Globe2,
  ImagePlus,
  MonitorSmartphone,
  Paintbrush,
  Rocket,
  ShieldCheck,
  Smartphone,
  UploadCloud
} from "lucide-react";
import "./styles.css";

type Asset = {
  name: string;
  size: number;
  dataUrl: string;
};

const iconSizes = [
  { name: "favicon-16x16.png", size: 16 },
  { name: "favicon-32x32.png", size: 32 },
  { name: "favicon-48x48.png", size: 48 },
  { name: "apple-touch-icon.png", size: 180 },
  { name: "android-chrome-192x192.png", size: 192 },
  { name: "android-chrome-512x512.png", size: 512 },
  { name: "maskable-icon-512x512.png", size: 512 }
];

function App() {
  const [name, setName] = useState("RocketLaunch");
  const [shortName, setShortName] = useState("Rocket");
  const [background, setBackground] = useState("#ff4f46");
  const [padding, setPadding] = useState(18);
  const [radius, setRadius] = useState(24);
  const [maskable, setMaskable] = useState(true);
  const [logo, setLogo] = useState<string>("");
  const [assets, setAssets] = useState<Asset[]>([]);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all(iconSizes.map((item) => renderIcon(item.name, item.size, background, padding, radius, logo, maskable))).then((next) => {
      if (!cancelled) setAssets(next);
    });
    return () => { cancelled = true; };
  }, [background, logo, maskable, padding, radius]);

  const preview192 = assets.find((asset) => asset.size === 192)?.dataUrl;
  const preview512 = assets.find((asset) => asset.name === "maskable-icon-512x512.png")?.dataUrl ?? assets.find((asset) => asset.size === 512)?.dataUrl;
  const manifest = useMemo(() => buildManifest(name, shortName, background, maskable), [background, maskable, name, shortName]);
  const snippet = useMemo(() => buildSnippet(background), [background]);

  async function handleUpload(file: File | undefined) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogo(String(reader.result));
    reader.readAsDataURL(file);
  }

  async function copyText(text: string, label: string) {
    await navigator.clipboard.writeText(text);
    setCopied(label);
    window.setTimeout(() => setCopied(""), 1300);
  }

  async function downloadZip() {
    const zip = new JSZip();
    for (const asset of assets) {
      zip.file(asset.name, asset.dataUrl.split(",")[1], { base64: true });
    }
    zip.file("manifest.webmanifest", manifest);
    zip.file("head-snippet.html", snippet);
    const blob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${slug(name)}-icons.zip`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <a data-google-vignette="false" href="/" className="brand">
          <span><Rocket size={24} /></span>
          <div>
            <h1>FaviconForge PWA</h1>
            <small>Favicon, app icon, and manifest generator</small>
          </div>
        </a>
        <nav aria-label="Primary">
          <a data-google-vignette="false" href="#studio">Studio</a>
          <a data-google-vignette="false" href="#manifest">Manifest</a>
          <a data-google-vignette="false" href="#export">Export</a>
        </nav>
        <button className="export-button" type="button" onClick={downloadZip}><Download size={17} /> Export ZIP</button>
      </header>

      <section className="workspace" id="studio">
        <aside className="control-rail">
          <section className="panel">
            <PanelTitle number="1" title="Source logo" icon={<UploadCloud size={18} />} />
            <label className="upload-zone">
              <input type="file" accept="image/*" onChange={(event) => handleUpload(event.target.files?.[0])} />
              {preview192 ? <img src={preview192} alt="Generated icon preview" /> : <ImagePlus size={34} />}
              <strong>{logo ? "Logo loaded locally" : "Upload SVG or PNG"}</strong>
              <span>Or use the generated launch mark</span>
            </label>
            <label className="text-field">
              <span>App / site name</span>
              <input value={name} onChange={(event) => setName(event.target.value)} maxLength={48} />
            </label>
            <label className="text-field">
              <span>Short name</span>
              <input value={shortName} onChange={(event) => setShortName(event.target.value)} maxLength={12} />
            </label>
          </section>

          <section className="panel">
            <PanelTitle number="2" title="Customize" icon={<Paintbrush size={18} />} />
            <label className="color-field">
              <span>Background</span>
              <input type="color" value={background} onChange={(event) => setBackground(event.target.value)} />
              <input value={background} onChange={(event) => setBackground(event.target.value)} />
            </label>
            <RangeField label="Padding" value={padding} setValue={setPadding} />
            <RangeField label="Corner radius" value={radius} setValue={setRadius} />
            <label className="toggle-row">
              <input type="checkbox" checked={maskable} onChange={(event) => setMaskable(event.target.checked)} />
              <span>Make maskable</span>
            </label>
          </section>
        </aside>

        <section className="preview-panel">
          <div className="panel-heading">
            <PanelTitle number="3" title="Preview across platforms" icon={<MonitorSmartphone size={18} />} />
            <span>Generated in this browser session</span>
          </div>
          <div className="preview-grid">
            <article className="browser-preview">
              <h2>Browser tab</h2>
              <div className="browser-chrome">
                <span />
                <span />
                <span />
                {preview192 && <img src={preview192} alt="" />}
                <strong>{name}</strong>
              </div>
              <div className="address">https://example.com</div>
            </article>
            <article className="phone-preview">
              <h2>iOS home screen</h2>
              <div>
                {preview192 && <img src={preview192} alt="" />}
                <span>{shortName}</span>
              </div>
            </article>
            <article className="android-preview">
              <h2>Android launcher</h2>
              <div>
                {preview192 && <img src={preview192} alt="" />}
                <span>{shortName}</span>
              </div>
            </article>
            <article className="mask-preview">
              <h2>Maskable safe area</h2>
              <div>
                {preview512 && <img src={preview512} alt="" />}
                <span className="safe-ring" />
              </div>
            </article>
          </div>
        </section>

        <aside className="output-panel" id="export">
          <div className="panel-heading compact">
            <PanelTitle number="4" title="Output" icon={<FileArchive size={18} />} />
          </div>
          <div className="file-list">
            {assets.map((asset) => (
              <article key={asset.name}>
                <FileCode2 size={17} />
                <strong>{asset.name}</strong>
                <span>{asset.size}x{asset.size}</span>
                <CheckCircle2 size={16} />
              </article>
            ))}
          </div>
          <section className="code-card" id="manifest">
            <div>
              <strong>manifest.webmanifest</strong>
              <button type="button" onClick={() => copyText(manifest, "manifest")}><Clipboard size={16} /> {copied === "manifest" ? "Copied" : "Copy"}</button>
            </div>
            <pre>{manifest}</pre>
          </section>
          <section className="code-card">
            <div>
              <strong>HTML head snippet</strong>
              <button type="button" onClick={() => copyText(snippet, "snippet")}><Clipboard size={16} /> {copied === "snippet" ? "Copied" : "Copy"}</button>
            </div>
            <pre>{snippet}</pre>
          </section>
          <button className="download-button" type="button" onClick={downloadZip}><Download size={18} /> Download all assets</button>
        </aside>
      </section>

      <section className="seo-panel">
        <div>
          <ShieldCheck size={20} />
          <h2>Favicon and PWA icon generator for launches</h2>
        </div>
        <p>Generate browser favicons, Apple touch icons, Android icons, maskable PWA icons, manifest JSON, and copy-ready head tags from one source logo.</p>
        <div className="seo-points">
          <span><Apple size={16} /> iOS icon</span>
          <span><Smartphone size={16} /> Android</span>
          <span><Globe2 size={16} /> Web manifest</span>
        </div>
      </section>

      <footer>
        <span>Icons are generated locally in your browser. Test final assets on real devices before launch.</span>
        <nav>
          <a data-google-vignette="false" href="/privacy.html">Privacy</a>
          <a data-google-vignette="false" href="/about.html">About</a>
        </nav>
      </footer>
    </main>
  );
}

function PanelTitle({ number, title, icon }: { number: string; title: string; icon: React.ReactNode }) {
  return (
    <div className="panel-title">
      <span>{number}</span>
      {icon}
      <strong>{title}</strong>
    </div>
  );
}

function RangeField({ label, value, setValue }: { label: string; value: number; setValue: (value: number) => void }) {
  return (
    <label className="range-field">
      <span>{label}<strong>{value}%</strong></span>
      <input type="range" min="0" max="40" value={value} onChange={(event) => setValue(Number(event.target.value))} />
    </label>
  );
}

async function renderIcon(name: string, size: number, background: string, padding: number, radius: number, logo: string, maskable: boolean): Promise<Asset> {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is unavailable.");
  const inset = Math.round(size * (maskable ? Math.max(padding, 18) : padding) / 100);
  roundedRect(ctx, 0, 0, size, size, Math.round(size * radius / 100));
  ctx.fillStyle = background;
  ctx.fill();

  if (logo) {
    const image = await loadImage(logo);
    const scale = Math.min((size - inset * 2) / image.width, (size - inset * 2) / image.height);
    const width = image.width * scale;
    const height = image.height * scale;
    ctx.drawImage(image, (size - width) / 2, (size - height) / 2, width, height);
  } else {
    drawLaunchMark(ctx, size, inset);
  }

  return { name, size, dataUrl: canvas.toDataURL("image/png") };
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
}

function drawLaunchMark(ctx: CanvasRenderingContext2D, size: number, inset: number) {
  ctx.save();
  ctx.translate(size / 2, size / 2);
  ctx.rotate(-0.55);
  ctx.fillStyle = "#ffffff";
  roundedRect(ctx, -size * 0.12, -size * 0.28, size * 0.24, size * 0.54, size * 0.12);
  ctx.fill();
  ctx.fillStyle = "#0f172a";
  ctx.beginPath();
  ctx.arc(0, -size * 0.11, size * 0.045, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.moveTo(-size * 0.14, size * 0.18);
  ctx.lineTo(-size * 0.28, size * 0.36);
  ctx.lineTo(-size * 0.02, size * 0.28);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(size * 0.14, size * 0.18);
  ctx.lineTo(size * 0.28, size * 0.36);
  ctx.lineTo(size * 0.02, size * 0.28);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
  ctx.fillStyle = "rgba(255,255,255,0.84)";
  ctx.beginPath();
  ctx.arc(inset * 0.75, size - inset * 0.65, Math.max(2, size * 0.035), 0, Math.PI * 2);
  ctx.fill();
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Image could not be loaded."));
    image.src = src;
  });
}

function buildManifest(name: string, shortName: string, background: string, maskable: boolean) {
  return JSON.stringify({
    name,
    short_name: shortName,
    start_url: "/",
    display: "standalone",
    background_color: "#ffffff",
    theme_color: background,
    icons: [
      { src: "/android-chrome-192x192.png", sizes: "192x192", type: "image/png" },
      { src: "/android-chrome-512x512.png", sizes: "512x512", type: "image/png" },
      ...(maskable ? [{ src: "/maskable-icon-512x512.png", sizes: "512x512", type: "image/png", purpose: "maskable" }] : [])
    ]
  }, null, 2);
}

function buildSnippet(background: string) {
  return [
    `<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">`,
    `<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png">`,
    `<link rel="apple-touch-icon" href="/apple-touch-icon.png">`,
    `<link rel="manifest" href="/manifest.webmanifest">`,
    `<meta name="theme-color" content="${background}">`
  ].join("\n");
}

function slug(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "favicon-pack";
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
